import * as i0 from '@angular/core';
import { InjectionToken, Directive, HostBinding, Input, Component, ViewEncapsulation, ChangeDetectionStrategy, Inject, NgModule } from '@angular/core';

/** Position of the actions inside the card. */
var PsCardActionsAlignment;
(function (PsCardActionsAlignment) {
    PsCardActionsAlignment["START"] = "start";
    PsCardActionsAlignment["END"] = "end";
})(PsCardActionsAlignment || (PsCardActionsAlignment = {}));

var PsCardBorder;
(function (PsCardBorder) {
    PsCardBorder["Positive"] = "positive";
    PsCardBorder["Negative"] = "negative";
    PsCardBorder["Neutral"] = "neutral";
    PsCardBorder["None"] = "none";
})(PsCardBorder || (PsCardBorder = {}));

var PsCardDisplayDensity;
(function (PsCardDisplayDensity) {
    PsCardDisplayDensity["Large"] = "large";
    PsCardDisplayDensity["Regular"] = "regular";
    PsCardDisplayDensity["Compact"] = "compact";
})(PsCardDisplayDensity || (PsCardDisplayDensity = {}));

/** @docs-private */
function PS_CARD_DEFAULT_OPTIONS_FACTORY() {
    return { dynamic: true };
}
/** Injection token that can be used to specify defaults for `ps-card`. */
const PS_CARD_DEFAULT_OPTIONS = new InjectionToken('ps-card-default-options', {
    providedIn: 'root',
    factory: PS_CARD_DEFAULT_OPTIONS_FACTORY
});
/**
 * Content of a card, needed as it's used as a selector in the API.
 * @docs-private
 */
class PsCardContentDirective {
    get psCardContent() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardContentDirective, selector: "ps-card-content", host: { properties: { "class.ps-card-content": "this.psCardContent" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-card-content'
                }]
        }], propDecorators: { psCardContent: [{
                type: HostBinding,
                args: ['class.ps-card-content']
            }] } });
/**
 * Title of a card, needed as it's used as a selector in the API.
 * @docs-private
 */
class PsCardTitleDirective {
    get psCardTitle() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardTitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardTitleDirective, selector: "ps-card-title, [ps-card-title], [psCardTitle]", host: { properties: { "class.ps-card-title": "this.psCardTitle" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-card-title, [ps-card-title], [psCardTitle]'
                }]
        }], propDecorators: { psCardTitle: [{
                type: HostBinding,
                args: ['class.ps-card-title']
            }] } });
/**
 * Sub-title of a card, needed as it's used as a selector in the API.
 * @docs-private
 */
class PsCardSubtitleDirective {
    get psCardSubtitle() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardSubtitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardSubtitleDirective, selector: "ps-card-subtitle, [ps-card-subtitle], [psCardSubtitle]", host: { properties: { "class.ps-card-subtitle": "this.psCardSubtitle" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardSubtitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-card-subtitle, [ps-card-subtitle], [psCardSubtitle]'
                }]
        }], propDecorators: { psCardSubtitle: [{
                type: HostBinding,
                args: ['class.ps-card-subtitle']
            }] } });
/**
 * Action section of a card, needed as it's used as a selector in the API.
 * @docs-private
 */
class PsCardActionsDirective {
    constructor() {
        /** Position of the actions inside the card. */
        this.align = PsCardActionsAlignment.START;
    }
    get psCardActions() {
        return true;
    }
    get isAlingedEnd() {
        return this.align === 'end';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardActionsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardActionsDirective, selector: "ps-card-actions", inputs: { align: "align" }, host: { properties: { "class.ps-card-actions": "this.psCardActions", "class.ps-card-actions-align-end": "this.isAlingedEnd" } }, exportAs: ["psCardActions"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardActionsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-card-actions',
                    exportAs: 'psCardActions'
                }]
        }], propDecorators: { align: [{
                type: Input
            }], psCardActions: [{
                type: HostBinding,
                args: ['class.ps-card-actions']
            }], isAlingedEnd: [{
                type: HostBinding,
                args: ['class.ps-card-actions-align-end']
            }] } });
/**
 * Footer of a card, needed as it's used as a selector in the API.
 * @docs-private
 */
class PsCardFooterDirective {
    get psCardFooter() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardFooterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardFooterDirective, selector: "ps-card-footer", host: { properties: { "class.ps-card-footer": "this.psCardFooter" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardFooterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-card-footer'
                }]
        }], propDecorators: { psCardFooter: [{
                type: HostBinding,
                args: ['class.ps-card-footer']
            }] } });
/**
 * Image used in a card, needed to add the ps- CSS styling.
 * @docs-private
 */
class PsCardImageDirective {
    get psCardImage() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardImageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardImageDirective, selector: "[ps-card-image], [psCardImage]", host: { properties: { "class.ps-card-image": "this.psCardImage" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-card-image], [psCardImage]'
                }]
        }], propDecorators: { psCardImage: [{
                type: HostBinding,
                args: ['class.ps-card-image']
            }] } });
/**
 * Image used in a card, needed to add the ps- CSS styling.
 * @docs-private
 */
class PsCardSmImageDirective {
    get psCardSmImage() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardSmImageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardSmImageDirective, selector: "[ps-card-sm-image], [psCardImageSmall]", host: { properties: { "class.ps-card-sm-image": "this.psCardSmImage" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardSmImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-card-sm-image], [psCardImageSmall]'
                }]
        }], propDecorators: { psCardSmImage: [{
                type: HostBinding,
                args: ['class.ps-card-sm-image']
            }] } });
/**
 * Image used in a card, needed to add the ps- CSS styling.
 * @docs-private
 */
class PsCardMdImageDirective {
    get psCardMdImage() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardMdImageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardMdImageDirective, selector: "[ps-card-md-image], [psCardImageMedium]", host: { properties: { "class.ps-card-md-image": "this.psCardMdImage" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardMdImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-card-md-image], [psCardImageMedium]'
                }]
        }], propDecorators: { psCardMdImage: [{
                type: HostBinding,
                args: ['class.ps-card-md-image']
            }] } });
/**
 * Image used in a card, needed to add the ps- CSS styling.
 * @docs-private
 */
class PsCardLgImageDirective {
    get psCardLgImage() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardLgImageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardLgImageDirective, selector: "[ps-card-lg-image], [psCardImageLarge]", host: { properties: { "class.ps-card-lg-image": "this.psCardLgImage" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardLgImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-card-lg-image], [psCardImageLarge]'
                }]
        }], propDecorators: { psCardLgImage: [{
                type: HostBinding,
                args: ['class.ps-card-lg-image']
            }] } });
/**
 * Large image used in a card, needed to add the ps- CSS styling.
 * @docs-private
 */
class PsCardXlImageDirective {
    get psCardXlImage() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardXlImageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardXlImageDirective, selector: "[ps-card-xl-image], [psCardImageXLarge]", host: { properties: { "class.ps-card-xl-image": "this.psCardXlImage" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardXlImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-card-xl-image], [psCardImageXLarge]'
                }]
        }], propDecorators: { psCardXlImage: [{
                type: HostBinding,
                args: ['class.ps-card-xl-image']
            }] } });
/**
 * Avatar image used in a card, needed to add the ps- CSS styling.
 * @docs-private
 */
class PsCardAvatarDirective {
    get psCardAvatar() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardAvatarDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsCardAvatarDirective, selector: "[ps-card-avatar], [psCardAvatar]", host: { properties: { "class.ps-card-avatar": "this.psCardAvatar" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardAvatarDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ps-card-avatar], [psCardAvatar]'
                }]
        }], propDecorators: { psCardAvatar: [{
                type: HostBinding,
                args: ['class.ps-card-avatar']
            }] } });
/**
 * A basic content container component that adds the styles of a Paysafe design card.
 *
 * While this component can be used alone, it also provides a number
 * of preset styles for common card sections, including:
 * - ps-card-title
 * - ps-card-subtitle
 * - ps-card-content
 * - ps-card-actions
 * - ps-card-footer
 */
class PsCardComponent {
    get psCard() {
        return true;
    }
    get hasPositiveBorder() {
        return this.border === PsCardBorder.Positive;
    }
    get hasNegativeBorder() {
        return this.border === PsCardBorder.Negative;
    }
    get hasNoBorder() {
        return this.border === PsCardBorder.None;
    }
    get isDensityRegular() {
        return this.cardDensity === PsCardDisplayDensity.Regular;
    }
    get isDensityCompact() {
        return this.cardDensity === PsCardDisplayDensity.Compact;
    }
    constructor(defaults) {
        this.cardDensity = PsCardDisplayDensity.Large;
        this.border = PsCardBorder.Neutral;
        this.dynamic = !!defaults.dynamic;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardComponent, deps: [{ token: PS_CARD_DEFAULT_OPTIONS }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsCardComponent, selector: "ps-card", inputs: { dynamic: "dynamic", cardDensity: "cardDensity", border: "border" }, host: { properties: { "class.ps-card": "this.psCard", "class.ps-card-dynamic": "this.dynamic", "class.ps-card-positive-border": "this.hasPositiveBorder", "class.ps-card-negative-border": "this.hasNegativeBorder", "class.ps-card-no-border": "this.hasNoBorder", "class.ps-card-regular-density": "this.isDensityRegular", "class.ps-card-compact-density": "this.isDensityCompact" } }, exportAs: ["psCard"], ngImport: i0, template: "<ng-content></ng-content>\n<ng-content select=\"ps-card-footer\"></ng-content>\n", styles: [".ps-card{transition:box-shadow .28s cubic-bezier(.4,0,.2,1);display:block;position:relative;padding:2rem}._ps-animation-noopable.ps-card{transition:none;animation:none}.ps-card.ps-card-regular-density{padding:1.5rem}.ps-card.ps-card-compact-density{padding:1rem}.ps-card .ps-divider-horizontal{position:absolute;left:0;width:100%}[dir=rtl] .ps-card .ps-divider-horizontal{left:auto;right:0}.ps-card .ps-divider-horizontal.ps-divider-inset{position:static;margin:0}[dir=rtl] .ps-card .ps-divider-horizontal.ps-divider-inset{margin-right:0}@media (-ms-high-contrast: active){.ps-card{outline:solid .0625rem}}.ps-card-actions,.ps-card-subtitle,.ps-card-content{display:block;margin-bottom:1rem}.ps-card-title{display:block;margin-bottom:.5rem}.ps-card-actions{margin-left:-1rem;margin-right:-1rem;padding:.5rem 0}.ps-card-actions .ps-button,.ps-card-actions .ps-raised-button{margin:0 .5rem}.ps-card-actions-align-end{display:flex;justify-content:flex-end}.ps-card-image{width:calc(100% + 4rem);margin:0 -2rem 1rem}.ps-card-footer{display:block;margin:0 -2rem -2rem}.ps-card-header{display:flex;flex-direction:row}.ps-card-header .ps-card-title{margin-bottom:.75rem}.ps-card-header-text{margin:0 2rem}.ps-card-avatar{height:2.5rem;width:2.5rem;border-radius:50%;flex-shrink:0;object-fit:cover}.ps-card-title-group{display:flex;justify-content:space-between}.ps-card-sm-image{width:5rem;height:5rem}.ps-card-md-image{width:7rem;height:7rem}.ps-card-lg-image{width:9.5rem;height:9.5rem}.ps-card-xl-image{width:15rem;height:15rem;margin:-.5rem}.ps-card-title-group>.ps-card-xl-image{margin:-.5rem 0 .5rem}@media (max-width: 599px){.ps-card-title-group{margin:0}.ps-card-xl-image{margin-left:0;margin-right:0}}.ps-card>:first-child,.ps-card-content>:first-child{margin-top:0}.ps-card>:last-child:not(.ps-card-footer),.ps-card-content>:last-child:not(.ps-card-footer){margin-bottom:0}.ps-card-image:first-child{margin-top:-2rem;border-top-left-radius:inherit;border-top-right-radius:inherit}.ps-card>.ps-card-actions:last-child{margin-bottom:-.5rem;padding-bottom:0}.ps-card-actions .ps-button:first-child,.ps-card-actions .ps-raised-button:first-child{margin-left:0;margin-right:0}.ps-card-title:not(:first-child),.ps-card-subtitle:not(:first-child){margin-top:-.25rem}.ps-card-header .ps-card-subtitle:not(:first-child){margin-top:-.5rem}.ps-card>.ps-card-xl-image:first-child{margin-top:-.5rem}.ps-card>.ps-card-xl-image:last-child{margin-bottom:-.5rem}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-card', exportAs: 'psCard', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content></ng-content>\n<ng-content select=\"ps-card-footer\"></ng-content>\n", styles: [".ps-card{transition:box-shadow .28s cubic-bezier(.4,0,.2,1);display:block;position:relative;padding:2rem}._ps-animation-noopable.ps-card{transition:none;animation:none}.ps-card.ps-card-regular-density{padding:1.5rem}.ps-card.ps-card-compact-density{padding:1rem}.ps-card .ps-divider-horizontal{position:absolute;left:0;width:100%}[dir=rtl] .ps-card .ps-divider-horizontal{left:auto;right:0}.ps-card .ps-divider-horizontal.ps-divider-inset{position:static;margin:0}[dir=rtl] .ps-card .ps-divider-horizontal.ps-divider-inset{margin-right:0}@media (-ms-high-contrast: active){.ps-card{outline:solid .0625rem}}.ps-card-actions,.ps-card-subtitle,.ps-card-content{display:block;margin-bottom:1rem}.ps-card-title{display:block;margin-bottom:.5rem}.ps-card-actions{margin-left:-1rem;margin-right:-1rem;padding:.5rem 0}.ps-card-actions .ps-button,.ps-card-actions .ps-raised-button{margin:0 .5rem}.ps-card-actions-align-end{display:flex;justify-content:flex-end}.ps-card-image{width:calc(100% + 4rem);margin:0 -2rem 1rem}.ps-card-footer{display:block;margin:0 -2rem -2rem}.ps-card-header{display:flex;flex-direction:row}.ps-card-header .ps-card-title{margin-bottom:.75rem}.ps-card-header-text{margin:0 2rem}.ps-card-avatar{height:2.5rem;width:2.5rem;border-radius:50%;flex-shrink:0;object-fit:cover}.ps-card-title-group{display:flex;justify-content:space-between}.ps-card-sm-image{width:5rem;height:5rem}.ps-card-md-image{width:7rem;height:7rem}.ps-card-lg-image{width:9.5rem;height:9.5rem}.ps-card-xl-image{width:15rem;height:15rem;margin:-.5rem}.ps-card-title-group>.ps-card-xl-image{margin:-.5rem 0 .5rem}@media (max-width: 599px){.ps-card-title-group{margin:0}.ps-card-xl-image{margin-left:0;margin-right:0}}.ps-card>:first-child,.ps-card-content>:first-child{margin-top:0}.ps-card>:last-child:not(.ps-card-footer),.ps-card-content>:last-child:not(.ps-card-footer){margin-bottom:0}.ps-card-image:first-child{margin-top:-2rem;border-top-left-radius:inherit;border-top-right-radius:inherit}.ps-card>.ps-card-actions:last-child{margin-bottom:-.5rem;padding-bottom:0}.ps-card-actions .ps-button:first-child,.ps-card-actions .ps-raised-button:first-child{margin-left:0;margin-right:0}.ps-card-title:not(:first-child),.ps-card-subtitle:not(:first-child){margin-top:-.25rem}.ps-card-header .ps-card-subtitle:not(:first-child){margin-top:-.5rem}.ps-card>.ps-card-xl-image:first-child{margin-top:-.5rem}.ps-card>.ps-card-xl-image:last-child{margin-bottom:-.5rem}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PS_CARD_DEFAULT_OPTIONS]
                }] }], propDecorators: { psCard: [{
                type: HostBinding,
                args: ['class.ps-card']
            }], dynamic: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.ps-card-dynamic']
            }], cardDensity: [{
                type: Input
            }], border: [{
                type: Input
            }], hasPositiveBorder: [{
                type: HostBinding,
                args: ['class.ps-card-positive-border']
            }], hasNegativeBorder: [{
                type: HostBinding,
                args: ['class.ps-card-negative-border']
            }], hasNoBorder: [{
                type: HostBinding,
                args: ['class.ps-card-no-border']
            }], isDensityRegular: [{
                type: HostBinding,
                args: ['class.ps-card-regular-density']
            }], isDensityCompact: [{
                type: HostBinding,
                args: ['class.ps-card-compact-density']
            }] } });
/**
 * Component intended to be used within the `<ps-card>` component. It adds styles for a
 * preset header section (i.e. a title, subtitle, and avatar layout).
 * @docs-private
 */
class PsCardHeaderComponent {
    get psCardHeader() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsCardHeaderComponent, selector: "ps-card-header", host: { properties: { "class.ps-card-header": "this.psCardHeader" } }, ngImport: i0, template: "<ng-content select=\"[ps-card-avatar], [psCardAvatar]\"></ng-content>\n<div class=\"ps-card-header-text\">\n  <ng-content\n    select=\"ps-card-title, ps-card-subtitle,\n      [ps-card-title], [ps-card-subtitle],\n      [psCardTitle], [psCardSubtitle]\"\n  ></ng-content>\n</div>\n<ng-content></ng-content>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-card-header', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content select=\"[ps-card-avatar], [psCardAvatar]\"></ng-content>\n<div class=\"ps-card-header-text\">\n  <ng-content\n    select=\"ps-card-title, ps-card-subtitle,\n      [ps-card-title], [ps-card-subtitle],\n      [psCardTitle], [psCardSubtitle]\"\n  ></ng-content>\n</div>\n<ng-content></ng-content>\n" }]
        }], propDecorators: { psCardHeader: [{
                type: HostBinding,
                args: ['class.ps-card-header']
            }] } });
/**
 * Component intended to be used within the `<ps-card>` component. It adds styles for a preset
 * layout that groups an image with a title section.
 * @docs-private
 */
class PsCardTitleGroupComponent {
    get psCardTitleGroup() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardTitleGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsCardTitleGroupComponent, selector: "ps-card-title-group", host: { properties: { "class.ps-card-title-group": "this.psCardTitleGroup" } }, ngImport: i0, template: "<div>\n  <ng-content\n    select=\"ps-card-title, ps-card-subtitle,\n      [ps-card-title], [ps-card-subtitle],\n      [psCardTitle], [psCardSubtitle]\"\n  ></ng-content>\n</div>\n<ng-content select=\"img\"></ng-content>\n<ng-content></ng-content>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardTitleGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-card-title-group', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div>\n  <ng-content\n    select=\"ps-card-title, ps-card-subtitle,\n      [ps-card-title], [ps-card-subtitle],\n      [psCardTitle], [psCardSubtitle]\"\n  ></ng-content>\n</div>\n<ng-content select=\"img\"></ng-content>\n<ng-content></ng-content>\n" }]
        }], propDecorators: { psCardTitleGroup: [{
                type: HostBinding,
                args: ['class.ps-card-title-group']
            }] } });

class PsCardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsCardModule, declarations: [PsCardComponent,
            PsCardHeaderComponent,
            PsCardTitleGroupComponent,
            PsCardContentDirective,
            PsCardTitleDirective,
            PsCardSubtitleDirective,
            PsCardActionsDirective,
            PsCardFooterDirective,
            PsCardSmImageDirective,
            PsCardMdImageDirective,
            PsCardLgImageDirective,
            PsCardImageDirective,
            PsCardXlImageDirective,
            PsCardAvatarDirective], exports: [PsCardComponent,
            PsCardHeaderComponent,
            PsCardTitleGroupComponent,
            PsCardContentDirective,
            PsCardTitleDirective,
            PsCardSubtitleDirective,
            PsCardActionsDirective,
            PsCardFooterDirective,
            PsCardSmImageDirective,
            PsCardMdImageDirective,
            PsCardLgImageDirective,
            PsCardImageDirective,
            PsCardXlImageDirective,
            PsCardAvatarDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsCardModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        PsCardComponent,
                        PsCardHeaderComponent,
                        PsCardTitleGroupComponent,
                        PsCardContentDirective,
                        PsCardTitleDirective,
                        PsCardSubtitleDirective,
                        PsCardActionsDirective,
                        PsCardFooterDirective,
                        PsCardSmImageDirective,
                        PsCardMdImageDirective,
                        PsCardLgImageDirective,
                        PsCardImageDirective,
                        PsCardXlImageDirective,
                        PsCardAvatarDirective
                    ],
                    declarations: [
                        PsCardComponent,
                        PsCardHeaderComponent,
                        PsCardTitleGroupComponent,
                        PsCardContentDirective,
                        PsCardTitleDirective,
                        PsCardSubtitleDirective,
                        PsCardActionsDirective,
                        PsCardFooterDirective,
                        PsCardSmImageDirective,
                        PsCardMdImageDirective,
                        PsCardLgImageDirective,
                        PsCardImageDirective,
                        PsCardXlImageDirective,
                        PsCardAvatarDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PS_CARD_DEFAULT_OPTIONS, PS_CARD_DEFAULT_OPTIONS_FACTORY, PsCardActionsAlignment, PsCardActionsDirective, PsCardAvatarDirective, PsCardBorder, PsCardComponent, PsCardContentDirective, PsCardDisplayDensity, PsCardFooterDirective, PsCardHeaderComponent, PsCardImageDirective, PsCardLgImageDirective, PsCardMdImageDirective, PsCardModule, PsCardSmImageDirective, PsCardSubtitleDirective, PsCardTitleDirective, PsCardTitleGroupComponent, PsCardXlImageDirective };
